import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setGlobalLogout } from './SocketContext';
import { BACKEND_URL } from '../utils/config';
import { attachAuthRefreshInterceptor, TRANSIENT_REFRESH_FAILURE } from '../utils/authRefreshInterceptor';
import { attachUiLanguageHeader } from '../utils/uiLanguageHeader';
import { notificationLanguageToSync } from '../utils/notificationLanguage';
import pushNotificationService from '../services/pushNotificationService';
import { identifyUser, resetPostHog, captureEvent } from '../services/posthog';
import { identifyUserGA4, resetGA4, trackEvent } from '../services/ga4';
import { apiErrorMessage } from '@/utils/apiError';
import { authError } from '@/utils/authError';
import i18n from '../i18n/config';
import { safeLocalStorage, safeSessionStorage } from '../utils/safeStorage';
import { pendingInviteCode, clearInviteCode } from '../utils/invite';

// Backend API URL from runtime config
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.timeout = 30000; // 30 seconds timeout
axios.defaults.withCredentials = true; // SECURITY: Send httpOnly cookies with every request
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      throw new Error(authError('serverConnection'));
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// =============================================================================
// TOKEN REFRESH INTERCEPTOR
// =============================================================================
// Shared with utils/axios.js's separate instance — see authRefreshInterceptor.js.
attachAuthRefreshInterceptor(axios);
attachUiLanguageHeader(axios);

export const AuthProvider = ({ children }) => {
  // Pre-populate user from localStorage for instant authenticated UI
  // while the token is verified in the background via /auth/me
  const [user, setUser] = useState(() => {
    try {
      const savedUser = safeLocalStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(null); // Token in memory only — httpOnly cookie is the real auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Only try /auth/me if there's evidence of a previous session
      // (cached user or refresh token). Prevents 401 redirect loops for guests.
      const hasCachedSession = safeLocalStorage.getItem('user') || safeLocalStorage.getItem('wefaqna_refresh_token');
      if (!hasCachedSession) {
        setLoading(false);
        return;
      }

      // Try /auth/me — httpOnly cookie is sent automatically via withCredentials
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        // Keep cached user fresh for instant UI on next page load
        safeLocalStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        const status = error.response?.status;

        // A 401 the interceptor deliberately let through is NOT a session end.
        // It means the refresh request itself could not complete (network,
        // timeout, 5xx) while the refresh token is still perfectly good.
        // Clearing here would throw away a live session over a bad moment —
        // the same mistake the mobile app was making.
        if (error?.[TRANSIENT_REFRESH_FAILURE]) {
          captureEvent('auth_session_end', {
            reason: 'retained_transient_refresh_failure',
          });
          setLoading(false);
          return;
        }

        // Only clear auth on authentication errors (401) or ban/suspension (403 with keywords)
        // Don't logout on rate limit (429), server errors (5xx), or network errors
        if (status === 401) {
          captureEvent('auth_session_end', { reason: 'auth_me_unauthorized' });
          safeLocalStorage.removeItem('wefaqna_refresh_token');
          safeLocalStorage.removeItem('user');
          setToken(null);
          setUser(null);
        } else if (status === 403) {
          // SECURITY: Only logout on ban/suspension 403s, not permission-denied 403s
          // Via the shared extractor so this is ALWAYS a string: the ban
          // check below is `message.includes(kw)`, and on an array that
          // silently becomes a membership test that never matches.
          const message = apiErrorMessage(error, '');
          // `error_code` is the real signal — the server sets it on exactly the
          // account-status refusals. The keyword list stays as the fallback for
          // a response that predates it (or one a proxy rewrote), but it cannot
          // be the primary test any more: these messages are translated now,
          // and "Votre compte a été banni" contains none of these words.
          const errorCode = error?.response?.data?.error_code;
          const banCodes = ['account_banned', 'account_suspended', 'account_deleted'];
          const banKeywords = ['حظر', 'banned', 'suspended', 'معلق', 'محظور', 'موقوف'];
          const isBanRelated = errorCode
            ? banCodes.includes(errorCode)
            : banKeywords.some(kw => message.includes(kw));

          if (isBanRelated) {
            safeLocalStorage.removeItem('wefaqna_refresh_token');
            safeLocalStorage.removeItem('user');
            setToken(null);
            setUser(null);
            safeSessionStorage.setItem('auth_error', message || authError('accountSuspendedOrBanned'));
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          // Non-ban 403s: stay logged in (e.g., feature restrictions)
        } else {
          // For other errors (429, 5xx, network), keep the user logged in
          // but set user data from localStorage if available
          const savedUser = safeLocalStorage.getItem('user');
          if (savedUser) {
            try {
              setUser(JSON.parse(savedUser));
            } catch (e) {
              // Invalid JSON, ignore
            }
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Re-read the account when the tab comes back to the foreground.
   *
   * `has_form` and `profile_preference` are derived by the server and then
   * CACHED here (and mirrored into localStorage), and until now `/auth/me` ran
   * exactly once per mount. `shouldHealCachedHasForm` repairs only the
   * `has_form === false` direction, so the opposite staleness had no cure short
   * of a full page reload: after support moves an account to the other side the
   * profile is soft-deleted and the side flips, yet an open tab keeps offering
   * the OLD side's form.
   *
   * This is the web half of BUG-322 — the mobile app does the same on resume,
   * and a fix that lands on one client only is how the two drift apart.
   *
   * Throttled, because visibilitychange fires on every alt-tab and this is a
   * correctness backstop, not a poll. It never logs anyone out: a failure
   * leaves the cached user exactly as it was, which is what happened before.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let lastCheck = 0;
    const MIN_GAP_MS = 60_000;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!safeLocalStorage.getItem('user')) return;
      const now = Date.now();
      if (now - lastCheck < MIN_GAP_MS) return;
      lastCheck = now;

      axios.get(`${API}/auth/me`).then((response) => {
        setUser(response.data);
        safeLocalStorage.setItem('user', JSON.stringify(response.data));
      }).catch(() => {
        // Deliberately silent. The interceptor already owns 401s, and every
        // other failure must leave the session untouched.
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Original phone-based login
  const login = async (phone, password) => {
    const response = await axios.post(`${API}/auth/login`, { phone, password });
    const { access_token, refresh_token: refreshToken, user: userData } = response.data;
    // SECURITY: access_token is set as httpOnly cookie by backend — don't store in localStorage
    if (refreshToken) safeLocalStorage.setItem('wefaqna_refresh_token', refreshToken);
    safeLocalStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token); // Keep in memory for backward compat (Authorization header fallback)
    setUser(userData);
    return userData;
  };

  // Unified login (phone OR email)
  const loginUnified = async (identifier, password) => {
    try {
      const response = await axios.post(`${API}/auth/login/unified`, { identifier, password });
      const { access_token, refresh_token: refreshToken, user: userData } = response.data;
      if (refreshToken) safeLocalStorage.setItem('wefaqna_refresh_token', refreshToken);
      safeLocalStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('login'));
      throw new Error(errorMessage);
    }
  };

  // Original phone-based registration
  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, refresh_token: refreshToken, user: newUser } = response.data;
      if (refreshToken) safeLocalStorage.setItem('wefaqna_refresh_token', refreshToken);
      safeLocalStorage.setItem('user', JSON.stringify(newUser));
      setToken(access_token);
      setUser(newUser);
      // Web↔mobile parity: mobile fires logSignUp(method, preference) on
      // register (auth_provider.dart:113). Mirror it so web signups show as a
      // GA4 conversion (reserved `sign_up` name) + a PostHog funnel event.
      // Fire-and-forget; no PII (profile_preference is bride/groom).
      captureEvent('sign_up', { method: 'phone', preference: newUser?.profile_preference });
      trackEvent('sign_up', { method: 'phone' });
      return newUser;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('register'));
      throw new Error(errorMessage);
    }
  };

  // Email-based registration
  const registerWithEmail = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register/email`, userData);
      const { access_token, refresh_token: refreshToken, user: newUser } = response.data;
      if (refreshToken) safeLocalStorage.setItem('wefaqna_refresh_token', refreshToken);
      safeLocalStorage.setItem('user', JSON.stringify(newUser));
      setToken(access_token);
      setUser(newUser);
      // Web↔mobile parity (see register() above) — same conversion signal.
      captureEvent('sign_up', { method: 'email', preference: newUser?.profile_preference });
      trackEvent('sign_up', { method: 'email' });
      return newUser;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('register'));
      throw new Error(errorMessage);
    }
  };

  const logout = useCallback(async () => {
    // Try to call backend logout to blacklist token + clear httpOnly cookie
    try {
      // Remove FCM token from server before logout
      await pushNotificationService.removeTokenFromServer(token);
    } catch (error) {
      // Ignore FCM token removal errors
    }
    try {
      // Backend clears httpOnly cookie and blacklists token
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      // Ignore errors - still clear local state
    }
    // Clear local storage (only refresh token + cached user remain). Also
    // wipe the offline message queue so a different user signing in on the
    // same device doesn't replay the previous user's queued messages.
    safeLocalStorage.removeItem('wefaqna_refresh_token');
    safeLocalStorage.removeItem('user');
    safeLocalStorage.removeItem('wefaqna_token'); // Clean up legacy key
    safeLocalStorage.removeItem('authToken'); // Clean up legacy key
    safeLocalStorage.removeItem('adminUser');
    safeLocalStorage.removeItem('wefaqna_offline_messages');
    setToken(null);
    setUser(null);
  }, [token]);

  // Logout from all devices
  const logoutAllDevices = async () => {
    try {
      await axios.post(`${API}/auth/logout-all-devices`, {});
      // Clear local storage after successful logout
      safeLocalStorage.removeItem('wefaqna_refresh_token');
      safeLocalStorage.removeItem('user');
      safeLocalStorage.removeItem('adminUser');
      setToken(null);
      setUser(null);
      return { success: true };
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('logout'));
      throw new Error(errorMessage);
    }
  };

  // PostHog + GA4 identify/reset on user state change (single source of
  // truth — covers login, register, restore-from-localStorage, and logout
  // flows without modifying each individually).
  useEffect(() => {
    if (user?.id) {
      const userProps = {
        profile_preference: user.profile_preference,
        has_form: user.has_form ?? false,
        governorate: user.governorate,
        id_verified: user.id_verified ?? false,
        auth_method: user.auth_method,
      };
      identifyUser(user.id, userProps);
      identifyUserGA4(user.id, userProps);
    } else {
      // user cleared (logout, expired session) — drop identification.
      resetPostHog();
      resetGA4();
    }
  }, [user?.id, user?.profile_preference, user?.has_form, user?.governorate, user?.id_verified, user?.auth_method]);

  // Register logout function with SocketContext for force logout
  useEffect(() => {
    setGlobalLogout(logout);
  }, [logout]);

  // Auto-register FCM token whenever an authenticated session is
  // established — independent of whether the JS token is in memory.
  // The /users/me/fcm-token endpoint authenticates via the httpOnly
  // cookie now (axios.defaults.withCredentials), so we don't need a
  // JS-readable token to call it. The previous `!token` guard was a
  // leftover from pre-cookie auth and broke registration for every
  // user who refreshed their browser (token state is null after reload
  // because httpOnly cookies are intentionally unreadable from JS) —
  // observed result: only ~7.8% of active users had FCM tokens.
  //
  // Still gates on `permission === 'granted'` — we only re-send the
  // token if the user has already accepted notifications. The opt-in
  // prompt for new users is a separate UI concern (PushPermissionPrompt).
  useEffect(() => {
    const registerFCMToken = async () => {
      if (!user) return;
      if (pushNotificationService.getPermissionStatus() !== 'granted') {
        return;
      }
      try {
        await pushNotificationService.initialize();
        await pushNotificationService.sendTokenToServer();
        console.log('[AuthContext] FCM token auto-registered');
      } catch (error) {
        // Silently fail - push notifications are optional
        console.warn('[AuthContext] FCM token registration failed:', error);
      }
    };
    registerFCMToken();
  }, [user]);

  // Mirror the language the user is actually reading to the server, once per
  // authenticated session.
  //
  // The toggle in Settings already PUTs on change, but that only covers a
  // change made while signed in. The gap it left: i18next resolves the
  // language from localStorage FIRST and the browser second, so a returning
  // user who picked German months ago reads German while Accept-Language
  // still says English. The backend fills an empty `users.locale` from
  // Accept-Language at sign-in, which would file that user as English and
  // send them English email forever.
  //
  // Notifications follow the language on screen (utils/notificationLanguage.js),
  // the way big apps do it: no question, the server just hears the language
  // the member is reading whenever it differs, sent as a choice so it can
  // REPLACE the stored one. Last device used wins. This replaced a dialog that
  // asked about the mismatch - the old sync was sent as a guess, which could only fill
  // an empty value, which is why the mismatch could exist at all.
  //
  // Best-effort: a failure costs nothing, because the next sign-in tries
  // again. The local copy is updated so this does not run twice.
  useEffect(() => {
    if (!user) return;
    const toSend = notificationLanguageToSync({
      stored: user.locale,
      onScreen: i18n.resolvedLanguage || i18n.language,
      supported: i18n.options?.supportedLngs,
    });
    if (!toSend) return;
    axios.put(`${API}/users/me/locale`, { locale: toSend, explicit: true })
      .then(() => setUser((current) => (current ? { ...current, locale: toSend } : current)))
      .catch(() => { /* silent — the UI is already in the right language */ });
    // `[user]` only. `i18n` is the module singleton, so React can never see
    // `i18n.resolvedLanguage` change and listing it did nothing (the build
    // warned about exactly that). Its resources are bundled and `.init()` runs
    // at import, so the language is resolved before any user exists; a change
    // made later in Settings is PUT there, explicitly.
  }, [user]);

  // Phone OTP
  const sendOTP = async (phone) => {
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('otpSend'));
      throw new Error(errorMessage);
    }
  };

  const verifyOTP = async (phone, otp) => {
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('otpVerify'));
      throw new Error(errorMessage);
    }
  };

  // Email OTP
  const sendEmailOTP = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/send-email-otp`, { email });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('otpSend'));
      throw new Error(errorMessage);
    }
  };

  const verifyEmailOTP = async (email, otp) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email-otp`, { email, otp });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('otpVerify'));
      throw new Error(errorMessage);
    }
  };

  // Firebase authentication (Google/Facebook)
  const loginWithFirebase = async (firebaseToken, provider) => {
    try {
      // Google is most sign-ups and used to drop the invitation; the server
      // uses the code only when this sign-in CREATES the account.
      const inviteCode = pendingInviteCode();
      const response = await axios.post(`${API}/auth/firebase`, {
        firebase_token: firebaseToken,
        provider,
        ...(inviteCode ? { referral_code: inviteCode } : {}),
      });
      clearInviteCode();
      const { access_token, refresh_token: refreshToken, user: userData } = response.data;
      if (refreshToken) safeLocalStorage.setItem('wefaqna_refresh_token', refreshToken);
      safeLocalStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('login'));
      throw new Error(errorMessage);
    }
  };

  // Password reset
  const requestPasswordReset = async (identifier, method = 'auto') => {
    try {
      const response = await axios.post(`${API}/auth/password-reset/request`, { identifier, method });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('passwordResetRequest'));
      throw new Error(errorMessage);
    }
  };

  const validateResetToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/password-reset/validate/${token}`);
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('invalidLink'));
      throw new Error(errorMessage);
    }
  };

  const resetPasswordWithToken = async (token, newPassword) => {
    try {
      const response = await axios.post(`${API}/auth/password-reset/verify-email`, {
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('passwordReset'));
      throw new Error(errorMessage);
    }
  };

  // Check the reset code on the code screen, where the code is typed.
  //
  // Until this existed, `verify-phone` was the only call and it takes the code
  // AND the new password together — so the "verify" button verified nothing and
  // a wrong code was reported on the password screen. Members read that as the
  // password being refused, changed it and tried again, and every retry spent
  // one of five code attempts and one of three hourly reset attempts.
  const verifyPasswordResetOTP = async (phone, otp) => {
    try {
      const response = await axios.post(`${API}/auth/password-reset/verify-otp`, {
        phone,
        otp,
      });
      return response.data;
    } catch (error) {
      throw new Error(apiErrorMessage(error, authError('otpInvalid')));
    }
  };

  const resetPasswordWithOTP = async (phone, otp, newPassword) => {
    try {
      const response = await axios.post(`${API}/auth/password-reset/verify-phone`, {
        phone,
        otp,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('passwordReset'));
      throw new Error(errorMessage);
    }
  };

  // Change phone number
  const requestPhoneChange = async (newPhone, currentPassword = null) => {
    try {
      const body = { new_phone: newPhone };
      if (currentPassword) {
        body.current_password = currentPassword;
      }
      const response = await axios.post(`${API}/users/change-phone/request`, body);
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('otpSendFailed'));
      throw new Error(errorMessage);
    }
  };

  const verifyPhoneChange = async (newPhone, otp) => {
    try {
      const response = await axios.post(`${API}/users/change-phone/verify`, {
        new_phone: newPhone,
        otp
      });
      // Update local user state with new phone
      if (user) {
        const updatedUser = { ...user, phone: newPhone };
        setUser(updatedUser);
        safeLocalStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return response.data;
    } catch (error) {
      const errorMessage = apiErrorMessage(error, authError('otpInvalid'));
      throw new Error(errorMessage);
    }
  };

  // Update user state locally (for preference changes, etc.)
  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    // Auth methods
    login,
    loginUnified,
    register,
    registerWithEmail,
    logout,
    logoutAllDevices,
    // Phone OTP
    sendOTP,
    verifyOTP,
    // Email OTP
    sendEmailOTP,
    verifyEmailOTP,
    // Firebase
    loginWithFirebase,
    // Password reset
    requestPasswordReset,
    validateResetToken,
    resetPasswordWithToken,
    resetPasswordWithOTP,
    verifyPasswordResetOTP,
    // Change phone
    requestPhoneChange,
    verifyPhoneChange,
    // User state management
    updateUser,
    refreshUser,
    // State
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
